import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { AIModel } from '../types';

export interface ModelListData {
  models: AIModel[];
  lastUpdated: string;
  count: number;
  validatedTop10?: AIModel[];
  validatedCount?: number;
  expandedCount?: number;
}

export interface TopTwinModel {
  originalOpenrouterId: string;
  originalName: string;
  originalCreator: string;
  intelligenceIndex: number;
  twinOpenrouterId: string;
  twinName: string;
  isTwin: boolean;
  lastUpdated: string;
}

export interface TopTwinsListData {
  models: TopTwinModel[];
  lastUpdated: string;
  count: number;
  matchingModelUsed: string;
  actualTwinsCount: number;
}

export interface ModelLists {
  top?: ModelListData;
  balanced?: ModelListData;
  speedy?: ModelListData;
  value?: ModelListData;        // Best IQ-per-dollar (intelligence rank + cost rank)
  cheap?: ModelListData;
  free?: ModelListData;
  freeUnified?: ModelListData;  // Unified free models (AA-ranked + HF-ranked, deduplicated by creator)
  topTwins?: TopTwinsListData;  // Streamlined twins for top intelligence models
}

export class ModelListsService {
  /**
   * Fetch all model lists from Firestore
   * Model lists are now consolidated in system/modelLists document
   */
  static async getModelLists(): Promise<ModelLists> {
    try {
      console.log('🔍 Fetching model lists from Firestore...');

      const modelListsRef = doc(db, 'system', 'modelLists');
      const modelListsSnap = await getDoc(modelListsRef);

      if (!modelListsSnap.exists()) {
        console.warn('⚠️ Model lists document not found in Firestore');
        return {};
      }

      const data = modelListsSnap.data() as ModelLists;
      
      console.log(`📊 Retrieved model lists from Firestore:`);
      if (data.top) console.log(`   - Top: ${data.top.count} models (updated: ${data.top.lastUpdated})`);
      if (data.balanced) console.log(`   - Balanced: ${data.balanced.count} models (updated: ${data.balanced.lastUpdated})`);
      if (data.speedy) console.log(`   - Speedy: ${data.speedy.count} models (updated: ${data.speedy.lastUpdated})`);
      if (data.cheap) console.log(`   - Cheap: ${data.cheap.count} models (updated: ${data.cheap.lastUpdated})`);
      if (data.free) console.log(`   - Free: ${data.free.count} models (updated: ${data.free.lastUpdated})`);
      if (data.freeUnified) console.log(`   - Free Unified: ${data.freeUnified.count} models (updated: ${data.freeUnified.lastUpdated})`);
      if (data.topTwins) console.log(`   - Top Twins: ${data.topTwins.count} models (${data.topTwins.actualTwinsCount} actual twins, updated: ${data.topTwins.lastUpdated})`);

      return data;
    } catch (error) {
      console.error('❌ Error fetching model lists from Firestore:', error);
      throw error;
    }
  }

  /**
   * Fetch a specific model list by name
   */
  static async getModelList(listName: 'top' | 'balanced' | 'speedy' | 'cheap' | 'free' | 'freeUnified'): Promise<AIModel[]> {
    try {
      console.log(`🔍 Fetching ${listName} models from Firestore...`);

      const modelListsRef = doc(db, 'system', 'modelLists');
      const modelListsSnap = await getDoc(modelListsRef);

      if (!modelListsSnap.exists()) {
        console.warn('⚠️ Model lists document not found in Firestore');
        return [];
      }

      const data = modelListsSnap.data() as ModelLists;
      const listData = data[listName];

      if (!listData) {
        console.warn(`⚠️ ${listName} list not found in model lists`);
        return [];
      }

      const models = listData.models || [];
      
      console.log(`📊 Retrieved ${models.length} ${listName} models from Firestore`);
      console.log(`📅 Last updated: ${listData.lastUpdated}`);

      return models;
    } catch (error) {
      console.error(`❌ Error fetching ${listName} models from Firestore:`, error);
      throw error;
    }
  }

  /**
   * Fetch top models (convenience method)
   */
  static async getTopModels(): Promise<AIModel[]> {
    return this.getModelList('top');
  }

  /**
   * Fetch balanced models (convenience method)
   */
  static async getBalancedModels(): Promise<AIModel[]> {
    return this.getModelList('balanced');
  }

  /**
   * Fetch speedy models (convenience method)
   */
  static async getSpeedyModels(): Promise<AIModel[]> {
    return this.getModelList('speedy');
  }

  /**
   * Fetch cheap models (convenience method)
   */
  static async getCheapModels(): Promise<AIModel[]> {
    return this.getModelList('cheap');
  }

  /**
   * Fetch free models (convenience method)
   */
  static async getFreeModels(): Promise<AIModel[]> {
    return this.getModelList('free');
  }

  /**
   * Fetch unified free models (convenience method)
   * Returns AA-ranked free models first, then HF+context ranked free models
   * All deduplicated by model creator (no duplicates)
   */
  static async getFreeUnifiedModels(): Promise<AIModel[]> {
    return this.getModelList('freeUnified');
  }

  /**
   * Fetch top twins list (streamlined equivalents of top intelligence models)
   * Used for faster synthesis when prioritize=intelligence and level 1 peer review is enabled
   */
  static async getTopTwins(): Promise<TopTwinModel[]> {
    try {
      console.log('🔍 Fetching top twins from Firestore...');

      const modelListsRef = doc(db, 'system', 'modelLists');
      const modelListsSnap = await getDoc(modelListsRef);

      if (!modelListsSnap.exists()) {
        console.warn('⚠️ Model lists document not found in Firestore');
        return [];
      }

      const data = modelListsSnap.data() as ModelLists;
      const twinsData = data.topTwins;

      if (!twinsData) {
        console.warn('⚠️ topTwins list not found in model lists');
        return [];
      }

      const twins = twinsData.models || [];
      
      console.log(`📊 Retrieved ${twins.length} top twins from Firestore`);
      console.log(`   - Actual twins: ${twinsData.actualTwinsCount}`);
      console.log(`   - Fallbacks to original: ${twins.length - twinsData.actualTwinsCount}`);
      console.log(`📅 Last updated: ${twinsData.lastUpdated}`);

      return twins;
    } catch (error) {
      console.error('❌ Error fetching top twins from Firestore:', error);
      throw error;
    }
  }

  /**
   * Get the twin OpenRouter ID for a given original model
   * Returns the original ID if no twin mapping exists
   * @param originalOpenrouterId - The original top model's OpenRouter ID
   */
  static async getTwinForModel(originalOpenrouterId: string): Promise<string> {
    try {
      const twins = await this.getTopTwins();
      const twinEntry = twins.find(t => t.originalOpenrouterId === originalOpenrouterId);
      
      if (twinEntry && twinEntry.isTwin) {
        console.log(`🔄 Found twin for ${originalOpenrouterId}: ${twinEntry.twinOpenrouterId}`);
        return twinEntry.twinOpenrouterId;
      }
      
      console.log(`📌 No twin for ${originalOpenrouterId}, using original`);
      return originalOpenrouterId;
    } catch (error) {
      console.warn(`⚠️ Error getting twin for ${originalOpenrouterId}, using original:`, error);
      return originalOpenrouterId;
    }
  }
}

